import type { LengthValue, PrintJobSnapshot, RenderedPrintDocument, ToHtmlOptions } from "../core/types";
import { orientPageSize, toCssLength } from "../core/units";
import { escapeHtml } from "../utils/html";

// Generate the shared stylesheet for preview and print. Preview-specific flags
// can make page boundaries and page numbers easier to inspect during debugging.
export function createPrintStyles(snapshot: PrintJobSnapshot, options: ToHtmlOptions = {}): string {
  const mode = options.mode ?? "preview";
  const oriented = orientPageSize(snapshot.pageSize, snapshot.orientation);
  const size = snapshot.pageSize.name
    ? `${snapshot.pageSize.name} ${snapshot.orientation}`
    : `${toCssLength(oriented.width)} ${toCssLength(oriented.height)}`;
  const margin = `${toCssLength(snapshot.margins.top)} ${toCssLength(snapshot.margins.right)} ${toCssLength(snapshot.margins.bottom)} ${toCssLength(snapshot.margins.left)}`;
  const documentPadding = mode === "preview" ? margin : "0";
  const background = mode === "preview" ? "#eef2f7" : "#ffffff";
  const pageShadow = mode === "preview" ? "box-shadow:0 10px 30px rgba(15,23,42,0.12);" : "";
  const pageGap = options.showPageGap === false ? "0" : "18px";
  const autoHintBackground = options.showAutoPaginationHints ? "linear-gradient(to bottom, transparent calc(100% - 1px), rgba(148, 163, 184, 0.35) calc(100% - 1px))" : "none";
  const autoHintSize = options.showAutoPaginationHints ? `100% calc(${toCssLength(oriented.height)} + ${pageGap})` : "auto";

  return `
    :root {
      --printer-page-width: ${toCssLength(oriented.width)};
      --printer-page-height: ${toCssLength(oriented.height)};
      --printer-page-gap: ${pageGap};
      --printer-font-family: ${snapshot.fontFamily ?? "Inter, Arial, sans-serif"};
    }

    * {
      box-sizing: border-box;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: ${background};
      color: #111827;
      font-family: var(--printer-font-family);
    }

    body {
      min-height: 100vh;
    }

    .printer-pages {
      display: grid;
      gap: var(--printer-page-gap);
      padding-bottom: var(--printer-page-gap);
      background-image: ${autoHintBackground};
      background-size: ${autoHintSize};
      background-repeat: repeat-y;
    }

    .printer-document {
      width: var(--printer-page-width);
      min-height: var(--printer-page-height);
      margin: 16px auto;
      padding: ${documentPadding};
      background: #ffffff;
      position: relative;
      ${pageShadow}
    }

    .printer-page-content {
      position: relative;
      width: 100%;
      min-height: calc(var(--printer-page-height) - ${toCssLength(snapshot.margins.top)} - ${toCssLength(snapshot.margins.bottom)});
      overflow: hidden;
    }

    .printer-flow {
      position: relative;
      z-index: 1;
    }

    .printer-overlay {
      position: absolute;
      inset: 0;
      z-index: 2;
    }

    .printer-page-number {
      position: absolute;
      top: 8px;
      right: 12px;
      color: #64748b;
      font-size: 12px;
    }

    .printer-flow > * + * {
      margin-top: 12px;
    }

    .printer-element {
      position: relative;
      break-inside: auto;
    }

    .printer-element--keep-together {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .printer-element--page-break-before {
      break-before: page;
      page-break-before: always;
    }

    .printer-element--page-break-after {
      break-after: page;
      page-break-after: always;
    }

    .printer-page-break {
      break-before: page;
      page-break-before: always;
      height: 0;
      margin: 0;
      border: 0;
    }

    .printer-image {
      max-width: 100%;
      display: block;
    }

    .printer-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 14px;
    }

    .printer-table thead {
      display: table-header-group;
    }

    .printer-table--no-repeat-header thead {
      display: table-row-group;
    }

    .printer-table tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .printer-table__header,
    .printer-table__cell {
      border: 1px solid #d1d5db;
      padding: 8px 10px;
      vertical-align: top;
      word-break: break-word;
    }

    .printer-table__header {
      background: #f3f4f6;
      font-weight: 600;
    }

    .printer-table--striped .printer-table__row:nth-child(even) {
      background: #f9fafb;
    }

    .printer-code,
    .printer-qrcode {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      max-width: 100%;
    }

    .printer-code svg,
    .printer-qrcode svg {
      display: block;
      max-width: 100%;
      height: auto;
    }

    @page {
      size: ${size};
      margin: ${margin};
    }

    @media print {
      html,
      body {
        background: #ffffff;
      }

      .printer-pages {
        display: block;
        gap: 0;
        padding: 0;
        background: none;
      }

      .printer-document {
        margin: 0;
        box-shadow: none;
        min-height: auto;
        page-break-after: auto;
      }

      .printer-document--manual-break-after {
        page-break-after: always;
      }

      .printer-page-content {
        min-height: auto;
        overflow: visible;
      }

      .printer-page-number {
        display: none;
      }
    }
  `;
}

// Wrap the final CSS and body markup into a full HTML document that iframe
// preview and iframe print can both consume without extra host page coupling.
export function renderDocumentEnvelope(
  snapshot: PrintJobSnapshot,
  body: string,
  cssText: string,
  pageCount: number
): RenderedPrintDocument {
  const html = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(snapshot.title)}</title>
    <style>${cssText}</style>
  </head>
  <body>
    ${body}
  </body>
</html>`;

  return {
    html,
    cssText,
    title: snapshot.title,
    pageCount
  };
}
